import { afterNextRender, afterRenderEffect, Component, DestroyRef, ElementRef, inject, input, viewChild } from "@angular/core";
import { drawStroke, fitStrokes, prepareCanvas, type SignatureStroke } from "./signature-canvas";

const VIEW_INK_PX = 1.25;

/**
 * Read-only rendering of a captured signature, trimmed to its ink and fitted into this element's box.
 * It's a canvas, so ngx-print turns it into an image when printing and html2canvas captures it for PDFs.
 */
@Component({
  selector: "lib-signature-view",
  template: `<canvas #canvas role="img" [attr.aria-label]="label() + (strokes().length ? ' signature' : ' signature, not signed')"></canvas>`,
  styles: `
    :host {
      display: block;
      height: 0.45in;
    }
    canvas {
      display: block;
      width: 100%;
      height: 100%;
    }
  `,
})
export class SignatureView {
  readonly label = input.required<string>();
  readonly strokes = input<SignatureStroke[]>([]);

  private readonly canvas = viewChild.required<ElementRef<HTMLCanvasElement>>("canvas");

  constructor() {
    const destroyRef = inject(DestroyRef);
    afterNextRender(() => {
      const observer = new ResizeObserver(() => this.#redraw());
      observer.observe(this.canvas().nativeElement);
      destroyRef.onDestroy(() => observer.disconnect());
    });
    afterRenderEffect({ write: () => this.#redraw(this.strokes()) });
  }

  #redraw(strokes = this.strokes()) {
    const canvas = this.canvas().nativeElement;
    const ctx = prepareCanvas(canvas);
    if (!ctx || !strokes.length) return;
    const { scale, dx, dy } = fitStrokes(strokes, canvas.clientWidth, canvas.clientHeight);
    ctx.translate(dx, dy);
    ctx.scale(scale, scale);
    // Same on-screen ink weight however far the signature was shrunk.
    for (const stroke of strokes) drawStroke(ctx, stroke, VIEW_INK_PX / scale);
  }
}
